import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  User,
  Calendar,
  ArrowLeft,
  Settings,
  Download
} from 'lucide-react';
import { PlayerOverview } from '@/components/analytics/PlayerOverview';
import { PerformanceCharts } from '@/components/analytics/PerformanceCharts';
import { ComparativeAnalysis } from '@/components/analytics/ComparativeAnalysis';
import { AIRecommendations } from '@/components/analytics/AIRecommendations';
import { TeamAnalyticsOverview } from '@/components/analytics/TeamAnalyticsOverview';
import { useAuth } from '@/hooks/useAuth';

export function PlayerAnalytics() {
  const { playerId } = useParams<{ playerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, organization } = useAuth();
  const [timeRange, setTimeRange] = useState(searchParams.get('timeRange') || '30d');
  const [_view, _setView] = useState<'individual' | 'team'>('individual');

  const { data: playerData, isLoading: playerLoading } = useQuery({
    queryKey: ['analytics', 'player', playerId, timeRange],
    queryFn: async () => {
      if (!playerId || playerId === 'team') return null;
      
      // Mock player analytics data based on PRD specifications
      return {
        player: {
          id: playerId,
          name: 'Alex Johnson',
          avatar: undefined,
          role: 'PLAYER',
          joinDate: '2024-01-15',
          age: 16,
          battingStyle: 'Right-handed',
          specialties: ['Drive', 'Pull Shot'],
          coach: 'Sarah Wilson'
        },
        overview: {
          personalBests: {
            maxDistance: 98.5,
            bestAccuracy: 89.2,
            longestSession: 85,
            consistencyStreak: 12
          },
          currentForm: 'excellent' as const,
          improvementAreas: ['Cut Shot Technique', 'Spin Bowling', 'Late Cut'],
          strengths: ['Powerful Drives', 'Consistent Timing', 'Quick Feet'],
          goalProgress: [
            {
              id: '1',
              title: 'Improve Average Distance',
              target: 85,
              current: 78.5,
              progress: 92.4,
              deadline: '2024-03-01',
              status: 'on_track' as const
            },
            {
              id: '2',
              title: 'Master Cut Shot',
              target: 80,
              current: 65,
              progress: 81.3,
              deadline: '2024-02-15',
              status: 'at_risk' as const
            },
            {
              id: '3',
              title: 'Consistency Score 90%',
              target: 90,
              current: 87,
              progress: 96.7,
              deadline: '2024-01-30',
              status: 'ahead' as const
            }
          ]
        },
        performanceCharts: {
          distanceTrends: {
            data: [
              { date: '2024-01-12', distance: 72.5, quality: 7.8 },
              { date: '2024-01-14', distance: 75.2, quality: 8.1 },
              { date: '2024-01-16', distance: 73.8, quality: 7.9 },
              { date: '2024-01-18', distance: 78.4, quality: 8.4 },
              { date: '2024-01-20', distance: 76.9, quality: 8.2 },
              { date: '2024-01-22', distance: 80.1, quality: 8.7 },
              { date: '2024-01-24', distance: 78.5, quality: 8.5 }
            ]
          },
          consistencyTrends: {
            data: [
              { date: '2024-01-12', consistency: 82.1 },
              { date: '2024-01-14', consistency: 84.3 },
              { date: '2024-01-16', consistency: 83.7 },
              { date: '2024-01-18', consistency: 86.2 },
              { date: '2024-01-20', consistency: 85.9 },
              { date: '2024-01-22', consistency: 88.1 },
              { date: '2024-01-24', consistency: 87.0 }
            ]
          },
          shotTypeBreakdown: {
            data: [
              { type: 'Drive', count: 145, percentage: 35.2, avgDistance: 82.4 },
              { type: 'Pull', count: 98, percentage: 23.8, avgDistance: 75.1 },
              { type: 'Cut', count: 67, percentage: 16.3, avgDistance: 68.9 },
              { type: 'Sweep', count: 52, percentage: 12.6, avgDistance: 71.2 },
              { type: 'Hook', count: 49, percentage: 11.9, avgDistance: 79.8 }
            ]
          },
          weeklyProgress: {
            data: [
              { week: 'Week 1', sessions: 4, avgQuality: 7.8, improvement: 12.5 },
              { week: 'Week 2', sessions: 5, avgQuality: 8.2, improvement: 15.1 },
              { week: 'Week 3', sessions: 3, avgQuality: 8.0, improvement: 8.7 },
              { week: 'Week 4', sessions: 6, avgQuality: 8.6, improvement: 18.3 }
            ]
          },
          techniqueRadar: {
            data: [
              { skill: 'Timing', value: 88, maxValue: 100 },
              { skill: 'Power', value: 85, maxValue: 100 },
              { skill: 'Accuracy', value: 82, maxValue: 100 },
              { skill: 'Footwork', value: 79, maxValue: 100 },
              { skill: 'Balance', value: 86, maxValue: 100 },
              { skill: 'Follow Through', value: 81, maxValue: 100 }
            ]
          }
        },
        comparativeAnalysis: {
          peerComparison: {
            players: [
              { name: 'Alex Johnson', distance: 78.5, quality: 8.5, isCurrentPlayer: true },
              { name: 'Sarah Chen', distance: 76.2, quality: 8.2 },
              { name: 'Mike Wilson', distance: 82.1, quality: 8.7 },
              { name: 'Emma Davis', distance: 74.8, quality: 8.0 },
              { name: 'Tom Brown', distance: 79.3, quality: 8.3 }
            ]
          },
          benchmarkAnalysis: {
            ageGroup: '15-17',
            skillLevel: 'Intermediate',
            benchmarks: {
              distance: { playerValue: 78.5, benchmark: 75.0, percentile: 78 },
              consistency: { playerValue: 87.0, benchmark: 82.0, percentile: 82 },
              accuracy: { playerValue: 84.2, benchmark: 80.0, percentile: 76 },
              improvement: { playerValue: 15.2, benchmark: 12.0, percentile: 89 }
            }
          },
          rankingPosition: {
            overall: { position: 3, total: 24, change: 1 },
            ageGroup: { position: 2, total: 8, change: 0 },
            team: { position: 1, total: 6, change: 1 }
          }
        },
        recommendations: {
          immediate: [
            {
              id: '1',
              category: 'technique' as const,
              priority: 'high' as const,
              title: 'Focus on Cut Shot Foot Positioning',
              description: 'Your cut shot timing is good, but foot positioning needs work. Practice getting closer to the ball.',
              evidence: ['Last 3 sessions show 15% lower accuracy on cut shots', 'Foot cam analysis shows late movement'],
              expectedImprovement: '12-15% accuracy increase in 2 weeks',
              drillSuggestions: [
                { name: 'Cone Drill for Footwork', duration: '15 minutes', difficulty: 'medium' as const },
                { name: 'Cut Shot Repetition', duration: '20 minutes', difficulty: 'hard' as const }
              ]
            },
            {
              id: '2',
              category: 'mental' as const,
              priority: 'medium' as const,
              title: 'Pre-Shot Routine Consistency',
              description: 'Establish a consistent pre-shot routine to improve concentration and timing.',
              evidence: ['Best shots come after 3-second pause', 'Rushed shots show 20% lower quality'],
              expectedImprovement: '8-10% overall quality improvement',
              drillSuggestions: [
                { name: 'Mindfulness Training', duration: '10 minutes', difficulty: 'easy' as const },
                { name: 'Routine Practice', duration: '5 minutes', difficulty: 'easy' as const }
              ]
            }
          ],
          shortTerm: [
            {
              id: '3',
              category: 'technique' as const,
              priority: 'high' as const,
              title: 'Develop Late Cut Variation',
              description: 'Add the late cut to your repertoire for better scoring options against spin.',
              timeline: '2-3 weeks',
              milestones: ['Master basic technique', 'Achieve 70% success rate', 'Use in match situations']
            },
            {
              id: '4',
              category: 'fitness' as const,
              priority: 'medium' as const,
              title: 'Improve Rotational Power',
              description: 'Focus on core strength and rotational exercises to increase shot power.',
              timeline: '4-6 weeks',
              milestones: ['Complete strength assessment', '15% power increase', 'Maintain technique quality']
            }
          ],
          longTerm: [
            {
              id: '5',
              category: 'strategy' as const,
              priority: 'high' as const,
              title: 'Develop Match-Specific Strategies',
              description: 'Build strategies for different match situations and bowling attacks.',
              timeline: 'Full season',
              goals: ['Create situation playbook', 'Practice under pressure', 'Achieve match consistency']
            },
            {
              id: '6',
              category: 'strategy' as const,
              priority: 'medium' as const,
              title: 'Mentorship Role Development',
              description: 'Take on mentorship role with junior players while continuing personal development.',
              timeline: '6 months',
              goals: ['Mentor 2 junior players', 'Lead team discussions', 'Maintain personal performance']
            }
          ]
        }
      };
    },
    enabled: !!playerId && playerId !== 'team'
  });

  const { data: teamData, isLoading: _teamLoading } = useQuery({
    queryKey: ['analytics', 'team', organization?.id, timeRange],
    queryFn: async () => {
      // Mock team analytics data based on PRD specifications
      return {
        teamOverview: {
          totalPlayers: 24,
          activeToday: 8,
          averageImprovement: 14.7,
          topPerformers: [
            { id: '1', name: 'Alex Johnson', avgQuality: 8.6, improvement: 18.3, sessions: 15 },
            { id: '2', name: 'Sarah Chen', avgQuality: 8.4, improvement: 16.8, sessions: 12 },
            { id: '3', name: 'Mike Wilson', avgQuality: 8.7, improvement: 15.2, sessions: 18 },
            { id: '4', name: 'Emma Davis', avgQuality: 8.1, improvement: 22.1, sessions: 10 }
          ],
          needsAttention: [
            { id: '5', name: 'Tom Brown', issue: 'Declining accuracy', lastSession: '2 days ago' },
            { id: '6', name: 'Lisa Wang', issue: 'Missed 4 sessions', lastSession: '1 week ago' },
            { id: '7', name: 'James Lee', issue: 'Plateau in progress', lastSession: '1 day ago' }
          ]
        },
        performanceDistribution: {
          skillLevelBreakdown: {
            'Beginner': 6,
            'Intermediate': 12,
            'Advanced': 6
          },
          improvementRates: {
            'High (>20%)': 5,
            'Moderate (10-20%)': 14,
            'Low (<10%)': 5
          },
          attendancePatterns: {
            'Excellent (>90%)': 8,
            'Good (70-90%)': 12,
            'Needs Improvement (<70%)': 4
          }
        },
        coachingInsights: {
          mostEffectiveDrills: [
            { name: 'Drive Technique', effectiveness: 92.3, usageCount: 156 },
            { name: 'Footwork Fundamentals', effectiveness: 89.7, usageCount: 134 },
            { name: 'Power Development', effectiveness: 87.1, usageCount: 98 },
            { name: 'Timing Practice', effectiveness: 85.9, usageCount: 142 }
          ],
          playerProgressRates: {
            averageRate: 14.7,
            topQuartile: 19.8,
            medianRate: 13.2,
            improvementFactors: ['Consistent attendance', 'Drill variety', 'Individual attention']
          },
          sessionOptimization: {
            optimalDuration: 45,
            optimalFrequency: '3-4 per week',
            peakPerformanceTime: '10:00-12:00',
            recommendedBreaks: 2
          }
        }
      };
    },
    enabled: !playerId || playerId === 'team'
  });

  const timeRangeOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 3 months' },
    { value: '1y', label: 'Last year' }
  ];

  const handleTimeRangeChange = (value: string) => {
    setTimeRange(value);
    setSearchParams(prev => {
      prev.set('timeRange', value);
      return prev;
    });
  };

  const isCoach = user?.role === 'COACH' || user?.role === 'ADMIN';

  if ((!playerId || playerId === 'team') && teamData) {
    return (
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Team Analytics</h1>
            <p className="text-muted-foreground">
              Academy-wide performance insights and coaching analytics
            </p>
          </div>
          
          <div className="flex items-center space-x-2 mt-4 sm:mt-0">
            <Select value={timeRange} onValueChange={handleTimeRangeChange}>
              <SelectTrigger className="w-[160px]">
                <Calendar className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timeRangeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>
        </div>

        <TeamAnalyticsOverview data={teamData} timeRange={timeRange} />
      </div>
    );
  }

  if (playerLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="h-8 bg-gray-200 rounded animate-pulse" />
        <div className="grid gap-4 md:grid-cols-4">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded animate-pulse" />
          ))}
        </div>
        <div className="h-64 bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }

  if (!playerData) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <User className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Player Not Found</h3>
            <p className="text-muted-foreground text-center">
              The requested player analytics could not be found.
            </p>
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Team Analytics
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4">
          <Avatar className="h-12 w-12">
            <AvatarImage src={playerData.player.avatar} />
            <AvatarFallback>
              {playerData.player.name.split(' ').map(n => n[0]).join('')}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{playerData.player.name}</h1>
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <span>{playerData.player.age} years old</span>
              <span>•</span>
              <span>{playerData.player.battingStyle}</span>
              <span>•</span>
              <span>Coach: {playerData.player.coach}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          <Select value={timeRange} onValueChange={handleTimeRangeChange}>
            <SelectTrigger className="w-[160px]">
              <Calendar className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {timeRangeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          
          {isCoach && (
            <Button variant="outline" size="icon">
              <Settings className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Player Overview Cards */}
      <PlayerOverview data={playerData.overview} />

      {/* Analytics Tabs */}
      <Tabs defaultValue="performance" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="comparison">Comparison</TabsTrigger>
          <TabsTrigger value="recommendations">AI Insights</TabsTrigger>
          <TabsTrigger value="goals">Goals</TabsTrigger>
        </TabsList>

        <TabsContent value="performance">
          <PerformanceCharts data={playerData.performanceCharts} timeRange={timeRange} />
        </TabsContent>

        <TabsContent value="comparison">
          <ComparativeAnalysis data={playerData.comparativeAnalysis} timeRange={timeRange} />
        </TabsContent>

        <TabsContent value="recommendations">
          <AIRecommendations data={playerData.recommendations} />
        </TabsContent>

        <TabsContent value="goals">
          <div className="grid gap-6">
            {playerData.overview.goalProgress.map((goal) => (
              <Card key={goal.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{goal.title}</CardTitle>
                    <Badge 
                      variant={
                        goal.status === 'ahead' ? 'default' : 
                        goal.status === 'on_track' ? 'secondary' : 
                        'destructive'
                      }
                    >
                      {goal.status === 'ahead' ? 'Ahead' : 
                       goal.status === 'on_track' ? 'On Track' : 
                       'At Risk'}
                    </Badge>
                  </div>
                  <CardDescription>
                    Target: {goal.target} | Current: {goal.current} | Deadline: {new Date(goal.deadline).toLocaleDateString()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progress</span>
                      <span>{goal.progress.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          goal.status === 'ahead' ? 'bg-green-500' : 
                          goal.status === 'on_track' ? 'bg-blue-500' : 
                          'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(goal.progress, 100)}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}